/**
 * Builder Hub — gecentraliseerde admin pagina voor alle builders.
 * Tabs voor: Workshop / Scrum / Beweegaanbod / Pakket.
 *
 * Elk tabblad gebruikt het generieke <BuilderTemplatesManager />.
 */
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sparkles, Zap, Activity, Package } from 'lucide-react';
import BuilderTemplatesManager from '@/components/builder/BuilderTemplatesManager';
import {
  workshopBuilderAPI,
  scrumBuilderAPI,
  beweegaanbodBuilderAPI,
  pakketBuilderAPI,
} from '@/lib/api';

const BUILDERS = [
  {
    key: 'workshop',
    label: 'Workshop',
    icon: Sparkles,
    api: workshopBuilderAPI,
    publicPath: '/workshop-builder',
    defaultColor: '#10B981',
    accentClass: 'border-emerald-200 bg-emerald-50/40',
    iconBgClass: 'bg-emerald-600',
    newButtonClass: 'bg-emerald-600 hover:bg-emerald-700',
    testIdPrefix: 'workshop',
  },
  {
    key: 'scrum',
    label: 'Scrum',
    icon: Zap,
    api: scrumBuilderAPI,
    publicPath: '/scrum-builder',
    defaultColor: '#6366F1',
    accentClass: 'border-indigo-200 bg-indigo-50/40',
    iconBgClass: 'bg-indigo-600',
    newButtonClass: 'bg-indigo-600 hover:bg-indigo-700',
    testIdPrefix: 'scrum',
  },
  {
    key: 'beweegaanbod',
    label: 'Beweegaanbod',
    icon: Activity,
    api: beweegaanbodBuilderAPI,
    publicPath: '/beweegaanbod-builder',
    defaultColor: '#3B82F6',
    accentClass: 'border-blue-200 bg-blue-50/40',
    iconBgClass: 'bg-blue-600',
    newButtonClass: 'bg-blue-600 hover:bg-blue-700',
    testIdPrefix: 'beweegaanbod',
  },
  {
    key: 'pakket',
    label: 'Pakket',
    icon: Package,
    api: pakketBuilderAPI,
    publicPath: '/pakket-builder',
    defaultColor: '#EC4899',
    accentClass: 'border-pink-200 bg-pink-50/40',
    iconBgClass: 'bg-pink-600',
    newButtonClass: 'bg-pink-600 hover:bg-pink-700',
    testIdPrefix: 'pakket',
  },
];

export default function BuilderHubPage() {
  const [active, setActive] = useState('workshop');

  return (
    <div className="space-y-5 fade-in" data-testid="builder-hub-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Builder Hub</h1>
        <p className="text-muted-foreground text-sm">
          Centraal beheer van alle publieke builders. Iedere builder heeft een eigen embeddable pagina, templates en bouwblokken — submissies komen automatisch binnen als Dienst + Lead in het CRM.
        </p>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="w-full justify-start flex-wrap h-auto bg-muted/50 p-1">
          {BUILDERS.map(b => {
            const Icon = b.icon;
            return (
              <TabsTrigger
                key={b.key}
                value={b.key}
                data-testid={`builder-tab-${b.key}`}
                className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Icon className="w-4 h-4" /> {b.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {BUILDERS.map(b => (
          <TabsContent key={b.key} value={b.key} className="mt-5">
            <BuilderTemplatesManager
              api={b.api}
              publicPath={b.publicPath}
              kindLabel={b.label}
              defaultColor={b.defaultColor}
              accentClass={b.accentClass}
              iconBgClass={b.iconBgClass}
              newButtonClass={b.newButtonClass}
              testIdPrefix={b.testIdPrefix}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
